import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { paymentApi } from '../services/api';

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  itemType: 'prompt' | 'agent';
  itemId: number;
  itemName: string;
  price: number;
  sellerName?: string;
}

const PaymentModal: React.FC<PaymentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  itemType,
  itemId,
  itemName,
  price,
  sellerName
}) => {
  const payType = 'wxpay'; // 只支持微信支付
  const [isCreating, setIsCreating] = useState(false);
  const [orderInfo, setOrderInfo] = useState<{
    orderNo: string;
    payUrl: string;
    qrcodeImg?: string;
  } | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  // 创建订单
  const handleCreateOrder = async () => {
    setIsCreating(true);
    try {
      const result = await paymentApi.createOrder({
        itemType,
        itemId,
        payType
      });

      if (result.success && result.data) {
        setOrderInfo({
          orderNo: result.data.orderNo,
          payUrl: result.data.payUrl,
          qrcodeImg: result.data.qrcodeImg
        });
        // 开始轮询订单状态
        setIsPolling(true);
      } else {
        toast.error(result.message || '创建订单失败');
      }
    } catch (error: any) {
      toast.error(error.message || '创建订单失败');
    } finally {
      setIsCreating(false);
    }
  };

  // 轮询订单状态
  useEffect(() => {
    if (!isPolling || !orderInfo?.orderNo) return;

    const pollInterval = setInterval(async () => {
      try {
        const result = await paymentApi.getOrder(orderInfo.orderNo, true);
        if (result.success && result.data?.status === 'paid') {
          setIsPolling(false);
          toast.success('支付成功！');
          onSuccess();
          onClose();
        }
      } catch (error) {
        console.error('轮询订单状态失败:', error);
      }
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [isPolling, orderInfo?.orderNo, onSuccess, onClose]);

  // 关闭时重置状态
  const handleClose = () => {
    setOrderInfo(null);
    setIsPolling(false);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* 头部 */}
            <div className="relative bg-gradient-to-r from-emerald-500 to-teal-500 p-6 text-white">
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-all"
              >
                <i className="fas fa-times text-white text-sm"></i>
              </button>
              <div className="text-center">
                <i className="fas fa-shopping-cart text-4xl mb-3"></i>
                <h2 className="text-xl font-bold">确认购买</h2>
              </div>
            </div>

            <div className="p-6">
              {/* 商品信息 */}
              <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4 mb-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary">
                        {itemType === 'prompt' ? '提示词' : '智能体'}
                      </span>
                    </div>
                    <h3 className="font-semibold text-gray-900 dark:text-white line-clamp-2 mb-1">
                      {itemName}
                    </h3>
                    {sellerName && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        <i className="fas fa-user mr-1"></i>
                        {sellerName}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-bold text-emerald-600">
                      ¥{price.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {!orderInfo ? (
                <>
                  {/* 支付方式 */}
                  <div className="mb-6">
                    <div className="flex items-center justify-center p-4 rounded-xl border-2 border-green-500 bg-green-50 dark:bg-green-900/20">
                      <i className="fab fa-weixin text-2xl text-green-500 mr-2"></i>
                      <span className="text-green-600 font-medium">微信支付</span>
                    </div>
                  </div>

                  {/* 确认支付按钮 */}
                  <button
                    onClick={handleCreateOrder}
                    disabled={isCreating}
                    className="w-full py-3 bg-gradient-to-r from-emerald-500 to-teal-500 text-white rounded-xl font-medium hover:shadow-lg hover:shadow-emerald-500/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isCreating ? (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2"></i>
                        创建订单中...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-lock mr-2"></i>
                        确认支付 ¥{price.toFixed(2)}
                      </>
                    )}
                  </button>
                </>
              ) : (
                <>
                  {/* 支付二维码/链接 */}
                  <div className="text-center mb-6">
                    {orderInfo.qrcodeImg ? (
                      <div className="inline-block p-4 bg-white rounded-xl shadow-sm">
                        <img
                          src={orderInfo.qrcodeImg}
                          alt="支付二维码"
                          className="w-48 h-48"
                        />
                      </div>
                    ) : (
                      <div className="py-8">
                        <i className="fas fa-qrcode text-6xl text-gray-300 mb-4"></i>
                        <p className="text-gray-500">正在加载二维码...</p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center justify-center text-sm text-gray-500 py-2">
                    {isPolling && (
                      <>
                        <i className="fas fa-spinner fa-spin mr-2 text-primary"></i>
                        正在等待支付结果...
                      </>
                    )}
                  </div>
                </>
              )}

              {/* 提示信息 */}
              <div className="mt-4 text-center text-xs text-gray-400">
                <i className="fas fa-shield-alt mr-1"></i>
                支付安全由第三方支付平台保障
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PaymentModal;

